import {useMutation, useQuery, useQueryClient} from "@tanstack/vue-query";
import axios from "axios";
import {errorToast, successToast} from "@/composables/useToasts";
import {computed} from "vue";

const useApprovalSettings = () => {
  const queryClient = useQueryClient();

  const saveSettingsMutation = useMutation({
    mutationFn: (data) => axios.post('/ajax/v2/approval-center/settings', data),
    onMutate: async (data) => {
      const queryKey = ['approval-setting-details'];

      await queryClient.cancelQueries({queryKey});

      const previousSettings = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (old) => {
        if (!old) {
          return old;
        }

        return {
          ...old,
          approval_time: data.approval_time,
        };
      });

      successToast('Your approval settings have been updated.');

      return {previousSettings};
    },
    onError: (_error, _variables, context) => {
      if (context?.previousSettings) {
        queryClient.setQueryData(['approval-setting-details'], context.previousSettings);
      }

      errorToast('Something went wrong');
    },
    onSettled: () => {
      queryClient.invalidateQueries({queryKey: ['approval-setting-details']});
    },
  });

  const saveBehavioursMutation = useMutation({
    mutationFn: (data) => axios.post('/ajax/v2/approval-center/behaviour/settings', data),
    onMutate: async ({setting_name: settingName, behaviour}) => {
      const queryKey = ['approval-setting-details'];

      await queryClient.cancelQueries({queryKey});

      const previousSettings = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (old) => {
        if (!old) {
          return old;
        }

        return {
          ...old,
          approval_behaviours: Object.values(old.approval_behaviours)?.map((item) => {
            if (item.name !== settingName) {
              return item;
            }

            return {
              ...item,
              behaviour,
            };
          }) ?? [],
        };
      });

      successToast('Your approval settings have been updated.');

      return {previousSettings};
    },
    onError: (_error, _variables, context) => {
      if (context?.previousSettings) {
        queryClient.setQueryData(['approval-setting-details'], context.previousSettings);
      }

      errorToast('Something went wrong');
    },
    onSettled: () => {
      queryClient.invalidateQueries({queryKey: ['approval-setting-details']});
    },
  });

  const saveFrequencyMutation = useMutation({
    mutationFn: (data) => axios.post('/ajax/v2/approval-center/settings/email-frequency', data),
    onMutate: async (data) => {
      const queryKey = ['approval-setting-details'];

      await queryClient.cancelQueries({queryKey});

      const previousSettings = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (old) => {
        if (!old) {
          return old;
        }

        return {
          ...old,
          website_email_setting: {
            ...old.website_email_setting,
            website_setting: data.email_frequency === 'agency_default' ? null : data.email_frequency,
            website_weekly_day: data.weekly_day ?? null,
          },
        };
      });

      successToast('Your email settings have been updated.');

      return {previousSettings};
    },
    onError: (_error, _variables, context) => {
      if (context?.previousSettings) {
        queryClient.setQueryData(['approval-setting-details'], context.previousSettings);
      }

      errorToast('Something went wrong');
    },
    onSettled: () => {
      queryClient.invalidateQueries({queryKey: ['approval-setting-details']});
      queryClient.invalidateQueries({queryKey: ['kit-email-recipients']});
    },
  });

  const settingsQuery = useQuery({
    queryKey: ['approval-setting-details'],
    queryFn: async () => {
      const response = await axios.get('/ajax/v2/approval-center/settings');

      return response.data.data;
    }
  });

  const websiteWeeklyDaySetting = computed(() => settingsQuery.data.value?.website_email_setting?.website_weekly_day ?? null);

  const kitEmailRecipientsQuery = useQuery({
    queryKey: ['kit-email-recipients'],
    queryFn: async () => {
      const response = await axios.get('/ajax/v2/approval-center/settings/seo-agent-email-recipients');

      return response.data.data;
    },
  });

  const saveKitEmailRecipientsMutation = useMutation({
    mutationFn: async (data) => {
      if (data.recipient_ids.length === 0) {
        errorToast('Please select at least one recipient.');

        throw new Error('At least one recipient is required');
      }

      return axios.post('/ajax/v2/approval-center/settings/seo-agent-email-recipients', data);
    },
    onMutate: async (data) => {
      const queryKey = ['kit-email-recipients'];

      await queryClient.cancelQueries({queryKey});

      const previousRecipients = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (old) => {
        if (!old) {
          return old;
        }

        return {
          ...old,
          selected_recipients: data.recipient_ids,
        };
      });

      if (data.recipient_ids.length !== 0) {
        successToast('Email recipients have been updated.');
      }

      return {previousRecipients};
    },
    onError: (_error, _variables, context) => {
      if (context?.previousRecipients) {
        queryClient.setQueryData(['kit-email-recipients'], context.previousRecipients);
      }

      if (error.message === 'At least one recipient is required') {
        errorToast(error.message);
        return;
      }

      errorToast('Something went wrong');
    },
    onSettled: () => {
      queryClient.invalidateQueries({queryKey: ['kit-email-recipients']});
    },
  });

  return {
    saveSettingsMutation,
    saveBehavioursMutation,
    saveFrequencyMutation,
    settingsQuery,
    websiteWeeklyDaySetting,
    kitEmailRecipientsQuery,
    saveKitEmailRecipientsMutation,
  };
};

export default useApprovalSettings;
